import { compact, groupBy, shuffle, take, uniq } from "lodash";
import { useEffect, useMemo, useState } from "react";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

const CATEGORIES = {
  progress: "Progress",
  feedback: "Feedback",
  conversations: "Conversations",
};

const ICONS = {
  [`${CATEGORIES.progress}`]: (
    <TopicflowIcon type="goal" className="w-4 h-4 text-gray-400" />
  ),
  [`${CATEGORIES.feedback}`]: (
    <TopicflowIcon type="feedback" className="w-4 h-4 text-gray-400" />
  ),
  [`${CATEGORIES.conversations}`]: (
    <TopicflowIcon type="meeting" className="w-4 h-4 text-gray-400" />
  ),
};

const AskAIEmptyPrompts = ({
  onStartChatSessionWithPrompt,
  isFullScreen,
}: {
  onStartChatSessionWithPrompt: (prompt: string) => void;
  isFullScreen: boolean;
}) => {
  const label = useLabel();
  const currentUser = currentUserVar();
  const directReports = assertEdgesNonNull(currentUser.directReports);
  const isManager = directReports.length > 0;
  const hasManagers = assertEdgesNonNull(currentUser.managers).length > 0;
  const currentOrganization = currentOrganizationVar();
  const goalFeatureEnabled = currentOrganization.featureFlags.goals;
  const recognitionsFeatureEnabled =
    currentOrganization.featureFlags.recognitions;
  const feedbacksFeatureEnabled = currentOrganization.featureFlags.feedbacks;
  const actionItemFeatureEnabled = currentOrganization.featureFlags.actionItems;
  const meetingsFeatureEnabled = currentOrganization.featureFlags.meetings;
  const randomReports = shuffle(directReports);
  const randomReport1 = randomReports.at(0 % randomReports.length);
  const randomReport2 = randomReports.at(1 % randomReports.length);
  const randomMReport3 = randomReports.at(2 % randomReports.length);

  const promptLibrary = useMemo(() => {
    return compact([
      goalFeatureEnabled && {
        prompt: "Give me an overview of my work for the last 14 days",
        category: CATEGORIES.progress,
      },
      goalFeatureEnabled && {
        prompt: "Help me update the progress of my opened goals",
        category: CATEGORIES.progress,
      },
      isManager &&
        goalFeatureEnabled && {
          prompt: `List the ${label("goal", { pluralize: true })} of my reports`,
          category: CATEGORIES.progress,
        },
      actionItemFeatureEnabled && {
        prompt: `What are my action items?`,
        category: CATEGORIES.progress,
      },
      goalFeatureEnabled && {
        prompt: `Draft a ${label("goal")} for the next quarter`,
        category: CATEGORIES.progress,
      },
      isManager &&
        recognitionsFeatureEnabled &&
        randomReport1 && {
          prompt: `Suggest a ${label("recognition")} for ${randomReport1.name}`,
          category: CATEGORIES.feedback,
        },
      feedbacksFeatureEnabled &&
        isManager &&
        randomReport2 && {
          prompt: `Suggest a feedback for ${randomReport2.name}`,
          category: CATEGORIES.feedback,
        },
      feedbacksFeatureEnabled && {
        prompt: "List the feedbacks I received in the past 6 months",
        category: CATEGORIES.feedback,
      },
      recognitionsFeatureEnabled && {
        prompt: `What is the last ${label("recognition")} I received?`,
        category: CATEGORIES.feedback,
      },
      meetingsFeatureEnabled &&
        hasManagers && {
          prompt: "Give me a summary of my last 1-on-1 with my manager?",
          category: CATEGORIES.conversations,
        },
      meetingsFeatureEnabled &&
        hasManagers && {
          prompt: "What should I talk about in my next 1-on-1 with my manager?",
          category: CATEGORIES.conversations,
        },
      meetingsFeatureEnabled &&
        isManager &&
        randomMReport3 && {
          prompt: `Show me the next 1-on-1 meeting I have with ${randomMReport3.name}`,
          category: CATEGORIES.conversations,
        },
    ]);
  }, []);
  const promptsByCategory = useMemo(() => {
    return groupBy(promptLibrary, "category");
  }, [promptLibrary]);
  const categories = uniq(Object.keys(promptsByCategory));
  const [category, setCategory] = useState<string>(categories[0]);
  const random3Prompts = useMemo(
    () => take(shuffle(promptLibrary), 3),
    [promptLibrary],
  );

  const filteredPromptLibrary = useMemo(() => {
    return promptLibrary.filter((prompt) => prompt.category === category);
  }, [promptLibrary, category]);

  const [hoveredPrompt, setHoveredPrompt] = useState<string | null>(null);

  useEffect(() => {
    setHoveredPrompt(filteredPromptLibrary[0]?.prompt ?? null);
  }, [category]);

  if (!isFullScreen) {
    return random3Prompts.length > 0 ? (
      <div className="mt-4 flex items-center justify-center gap-2 max-w-[900px] mx-auto flex-wrap">
        {random3Prompts.map((prompt) => (
          <div key={prompt.prompt}>
            <button
              className="whitespace-nowrap text-left px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600"
              onClick={() => {
                onStartChatSessionWithPrompt(prompt.prompt);
              }}
            >
              {prompt.prompt}
            </button>
          </div>
        ))}
      </div>
    ) : null;
  }
  return filteredPromptLibrary.length > 0 ? (
    <div className="w-full max-w-[900px] mx-auto mb-24">
      <div className="text-base leading-4 mb-4 font-medium">
        Prompts to get you started
      </div>
      <div className="flex items-center gap-2 mb-4">
        {categories.map((categoryItem) => (
          <Button
            text={categoryItem}
            onClick={() => {
              setCategory(categoryItem);
            }}
            className={classNames(category === categoryItem && "bg-gray-100")}
          >
            {ICONS[categoryItem]}
            {categoryItem}
          </Button>
        ))}
      </div>
      <div style={{ minHeight: `${5 * 36}px` }}>
        {filteredPromptLibrary.map((prompt) => (
          <div key={prompt.prompt}>
            <button
              className={classNames(
                "block w-full text-left px-3 py-2 rounded text-gray-600",
                hoveredPrompt === prompt.prompt && "bg-gray-100",
              )}
              onClick={() => {
                onStartChatSessionWithPrompt(prompt.prompt);
              }}
              onMouseEnter={() => {
                setHoveredPrompt(prompt.prompt);
              }}
              onMouseLeave={() => {
                setHoveredPrompt(null);
              }}
            >
              {prompt.prompt}
            </button>
          </div>
        ))}
      </div>
    </div>
  ) : null;
};

export default AskAIEmptyPrompts;
